<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  showSupportRequest: {
    type: Boolean,
    default: false,
  },
  showAgentConversation: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  supportTitle: {
    type: String,
    default: '',
  },
  supportDescription: {
    type: String,
    default: '',
  },
  conversationTitle: {
    type: String,
    default: '',
  },
  conversationDescription: {
    type: String,
    default: '',
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['openSupportRequest', 'startConversation']);

const { t } = useI18n();

const description = computed(() => {
  if (props.description) return props.description;

  if (props.showSupportRequest && !props.showAgentConversation) {
    return t('SUPPORT_REQUEST.CHOICE.SUPPORT_ONLY_DESCRIPTION');
  }

  if (!props.showSupportRequest && props.showAgentConversation) {
    return t('SUPPORT_REQUEST.CHOICE.CONVERSATION_ONLY_DESCRIPTION');
  }

  return t('SUPPORT_REQUEST.CHOICE.DESCRIPTION');
});

const title = computed(() => props.title || t('SUPPORT_REQUEST.CHOICE.TITLE'));
const supportTitle = computed(
  () => props.supportTitle || t('SUPPORT_REQUEST.CHOICE.SUPPORT_TITLE')
);
const supportDescription = computed(
  () =>
    props.supportDescription || t('SUPPORT_REQUEST.CHOICE.SUPPORT_DESCRIPTION')
);
const conversationTitle = computed(
  () => props.conversationTitle || t('CONVERSATION_START.CHOICE_TITLE')
);
const conversationDescription = computed(
  () =>
    props.conversationDescription || t('CONVERSATION_START.CHOICE_DESCRIPTION')
);
</script>

<template>
  <div class="flex flex-col" :class="compact ? 'gap-1.5' : 'gap-2'">
    <div class="flex flex-col gap-1">
      <h3
        class="font-semibold text-n-slate-12"
        :class="compact ? 'text-xs' : 'text-sm'"
      >
        {{ title }}
      </h3>
      <p
        class="text-n-slate-11"
        :class="compact ? 'text-[11px] leading-4' : 'text-xs leading-5'"
      >
        {{ description }}
      </p>
    </div>
    <button
      v-if="showSupportRequest"
      type="button"
      class="flex w-full items-center justify-between gap-3 rounded-lg border border-n-weak bg-[var(--widget-color)] px-3 text-left text-white transition-colors duration-200 hover:brightness-95"
      :class="compact ? 'py-2' : 'py-3'"
      @click="emit('openSupportRequest')"
    >
      <span class="min-w-0">
        <span
          class="block font-semibold"
          :class="compact ? 'text-xs' : 'text-sm'"
        >
          {{ supportTitle }}
        </span>
        <span
          class="block opacity-90"
          :class="compact ? 'text-[11px] leading-4' : 'text-xs leading-5'"
        >
          {{ supportDescription }}
        </span>
      </span>
      <i class="i-ph-arrow-right shrink-0 text-lg" />
    </button>
    <button
      v-if="showAgentConversation"
      type="button"
      class="flex w-full items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-left text-n-slate-12 transition-colors duration-200 hover:bg-n-alpha-2"
      :class="compact ? 'py-2' : 'py-3'"
      @click="emit('startConversation')"
    >
      <span class="min-w-0">
        <span
          class="block font-semibold"
          :class="compact ? 'text-xs' : 'text-sm'"
        >
          {{ conversationTitle }}
        </span>
        <span
          class="block text-n-slate-11"
          :class="compact ? 'text-[11px] leading-4' : 'text-xs leading-5'"
        >
          {{ conversationDescription }}
        </span>
      </span>
      <i class="i-ph-chat-circle shrink-0 text-lg" />
    </button>
  </div>
</template>
